import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
    MutableRefObject,
    ReactNode,
    forwardRef,
    memo,
    useCallback,
    useEffect,
} from 'react'
import type { StreamType } from '../../../../../types'
import { getNextOrPreviousModel } from '../../../../actions/getNextOrPreviousModel'
import { loadUserIfNeeded } from '../../../../actions/loadUserIfNeeded'
import { openMessage } from '../../../../actions/message'
import { openProfile } from '../../../../actions/nav'
import {
    openRoom,
    requestGroupShow,
    requestPrivate,
    requestVoyeur,
    setNextSurrogate,
    setPreviousSurrogate,
    streamPublicShow,
} from '../../../../actions/room'
import { updateRoomState } from '../../../../app-state/state.room'
import { AppStateType } from '../../../../constants/enums'
import { isLounge as isRoomLounge } from '../../../../mfcLogic/userProperties/user'
import { RoomView } from '../../../../state/modals'
import { openConfirmation } from '../../../../state/modals/state.confirmation'
import {
    openAuthenticationDrawer,
    openAutoplayOptionsDrawer,
    openProfileDrawer,
    openShareTipsDrawer,
    openTipsDrawer,
    openTokensDrawer,
} from '../../../../state/modals/state.drawer'
import { KeyEvent, registerKeyHandler } from '../../../../state/state.keys'
import { setPMChatIntentionallyClosedState } from '../../../../state/state.pm'
import i18n from '../../../../utilities/i18n'
import { closeRoomAndNavBack } from '../../../../utilities/router'
import { useAppState } from '../../../Hooks/useAppState'
import { useModelsLoadedAppState } from '../../../Hooks/useModelsLoadedAppState'
import { usePMChatSelector } from '../../../Hooks/usePMChat'
import { useRoomSelector } from '../../../Hooks/useRoomAppState'
import { useUserSelector } from '../../../Hooks/useUser'
import { useWhiteboardSelector } from '../../../Hooks/useWhiteboard'
import AccountDetails from '../../../Icons/AccountDetails'
import AutoPlayOptions from '../../../Icons/AutoPlayOptions'
import ChevronLeft from '../../../Icons/ChevronLeft'
import ChevronRight from '../../../Icons/ChevronRight'
import Close from '../../../Icons/Close'
import Exit from '../../../Icons/Exit'
import Group from '../../../Icons/Group'
import Message from '../../../Icons/Message'
import Private from '../../../Icons/Private'
import Room from '../../../Icons/Room'
import Spy from '../../../Icons/Spy'
import Tip from '../../../Icons/Tip'
import TipMenu from '../../../Icons/TipMenu'
import Videocam from '../../../Icons/Videocam'
import Whiteboard from '../../../Icons/Whiteboard'
import { getThemeSpacing } from '../../../styles/appTheme'
import { maxWindowWidth } from '../../../styles/breakpoints'
import { palette } from '../../../styles/colors'
import { APP_FONT, COMPONENT_HEIGHTS, PAPER_HEIGHTS } from '../../../theme'
import Notification from '../../Notification'
import AutoPlayActionButton from './AutoPlayActionButton'
import ChatActionButton from './ChatActionButton'
import { TruePrivateSwitch } from './TruePrivateSwitch'
import { NotAuthenticatedMessage, UpBuyMessage } from './UpBuyMessage'

const chatActionBarStyles = {
    display: 'flex',
    justifyContent: 'space-evenly',
    minHeight: COMPONENT_HEIGHTS.actionsBar,
    maxHeight: COMPONENT_HEIGHTS.actionsBar,
    // padding: `0 ${getThemeSpacing(0.5)}`,
    width: '100%',
    zIndex: PAPER_HEIGHTS.base,
    '& button': {
        fontFamily: APP_FONT,
        fontWeight: 500,
        [maxWindowWidth(374)]: {
            fontSize: 7.6,
        },
    },
} as const

const fullWindowCommonStyles = {
    display: 'flex',
    position: 'absolute',
    justifyContent: 'space-around',
    zIndex: PAPER_HEIGHTS.floatingActionBar,
    pointerEvents: 'none',
} as const

const fullWindowVerticalStyles = {
    ...fullWindowCommonStyles,
    right: 'calc(env(safe-area-inset-right, 0px) * 0.7)',
    top: 0,
    bottom: 0,
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100%',
    maxHeight: '100vh',
    '& button': {
        width: 64,
        height: 'auto',
        color: '#fff !important',
        pointerEvents: 'all',
        fontFamily: APP_FONT,
        overflow: 'visible',
        '& span': {
            fontWeight: 700,
        },
    },
    '& svg': {
        color: '#fff !important',
        WebkitTransform: 'translate(0px,0px)', // Safari svg flicker fix
    },
    filter: palette.overlayFilterDropShadow,
} as const

const fullWindowHorizontalStyles = {
    ...fullWindowCommonStyles,
    right: `calc(env(safe-area-inset-right, 0px) * 0.7)`,
    top: 'calc(env(safe-area-inset-top, 0px) * 0.7)',
    width: 'min(400px, 63%)',
    '& button': {
        color: '#fff !important',
        pointerEvents: 'all',
        fontFamily: APP_FONT,
        '& span': {
            fontWeight: 700,
        },
    },
    '& svg': {
        color: '#fff !important',
        WebkitTransform: 'translate(0px,0px)', // Safari svg flicker fix
    },
    filter: palette.overlayFilterDropShadow,
} as const

const ActionButton = styled('div')({
    position: 'relative',
    flex: '1 1 auto',
    maxHeight: 24,
    margin: 0,
    padding: 0,
})

const closeButtonStyles = css({
    flex: 0,
    padding: getThemeSpacing(1),
    minWidth: 40,
})

interface IMessageIcon {
    unreadPM: number
}

const MessageIcon = memo(({ unreadPM }: IMessageIcon) => (
    <ActionButton>
        {unreadPM > 0 && <Notification value={unreadPM} small />}
        <Message />
    </ActionButton>
))

interface IGroupShowIcon {
    countUsersRequesting: number
}

const GroupShowIcon = memo(({ countUsersRequesting }: IGroupShowIcon) => (
    <ActionButton>
        {countUsersRequesting > 0 && (
            <Notification value={countUsersRequesting} small />
        )}
        <Group />
    </ActionButton>
))

const requestTokenShow = (showType: StreamType) => {
    if (showType === 'group') return requestGroupShow
    if (showType === 'private') return requestPrivate
    if (showType === 'voyeur') return requestVoyeur
}

interface ChatActionsBarProps {
    fullWindow?: boolean
    id: number
    roomView: RoomView
    verticalLayout?: boolean
    video?: boolean
    whiteboard?: boolean
    floating?: boolean
    onWhiteboard: () => void
    onOpenRoom?: () => void
    onVideoToggle?: () => void
    onPreviousRoom?: () => void
    onNextRoom?: () => void
}

const ChatActionsBar = memo(
    forwardRef(
        (props: ChatActionsBarProps, ref: MutableRefObject<HTMLDivElement>) => {
            const {
                id,
                roomView,
                fullWindow,
                verticalLayout,
                video = false,
                onWhiteboard,
                onPreviousRoom,
                onVideoToggle,
                onOpenRoom,
                onNextRoom,
            } = props
            const isPm = roomView === RoomView.PM
            const isProfile = roomView === RoomView.Profile
            const isRoom = roomView === RoomView.Room
            const { authenticated: isAuthenticated } = useAppState(
                AppStateType.APP,
            )
            const tokens = useUserSelector('me', 'tokens')
            const selfUsername = useUserSelector('me', 'name')
            const isWhiteboardOpen = useWhiteboardSelector('open')

            const modelsLoaded = useModelsLoadedAppState()

            const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
            const roomID = useRoomSelector(id, 'id')
            const unreadPM = usePMChatSelector(id, 'unread')
            const groupShowRequesters = useUserSelector(
                roomID,
                'groupShowRequesters',
            )

            const isLounge = isRoomLounge(id)

            const foundModelID =
                isLounge && loungeSurrogateID ? loungeSurrogateID : id
            const name = useUserSelector(foundModelID, 'name')
            const slug = useUserSelector(foundModelID, 'slug')
            const tipmenus = useUserSelector(foundModelID, 'shareTipmenus')
            const isAway = useUserSelector(foundModelID, 'isAway')
            const hasWhiteboard = useUserSelector(foundModelID, 'hasWhiteboard')

            const isBroadcasting = useUserSelector(
                foundModelID,
                'isBroadcasting',
            )
            const isClubShow = useUserSelector(foundModelID, 'isClubShow')
            const isGroupShowDisabled = useUserSelector(
                foundModelID,
                'disableGroup',
            )
            const isPrivateShowDisabled = useUserSelector(
                foundModelID,
                'disablePrivate',
            )
            const isTruePrivateShowEnabled = useUserSelector(
                foundModelID,
                'enableTruePrivateOnly',
            )
            const isGroupShow = useUserSelector(foundModelID, 'isGroupShow')
            const isTruePrivate = useUserSelector(foundModelID, 'isTruePrivate')
            const isPrivateShow = useUserSelector(foundModelID, 'isPrivateShow')
            const isUserBanned = useUserSelector(foundModelID, 'banned')
            const streamType = useUserSelector(foundModelID, 'streamType')
            const requesting = useUserSelector(
                foundModelID,
                'privateShow',
                {},
                true,
                'requesting',
            )

            const isHiddenShow = isClubShow || isTruePrivate
            const isUserInClubRoom = streamType === 'club'
            const isUserInGroupRoom = streamType === 'group'
            const isUserInPrivateRoom = streamType === 'private'
            const isUserInVoyeurRoom = streamType === 'voyeur'
            const isUserInNonPublicRoom =
                isUserInClubRoom ||
                isUserInGroupRoom ||
                isUserInVoyeurRoom ||
                isUserInPrivateRoom
            const isUserInPmShow =
                streamType === 'private' ||
                streamType === 'voyeur' ||
                streamType === 'group' ||
                streamType === 'club'
            const isUserWaiting = !!groupShowRequesters?.includes(selfUsername)
            const countUsersRequestingGroupShow =
                groupShowRequesters?.length || 0
            const isGroupShowPending = countUsersRequestingGroupShow > 0
            const hasEnoughTokens = tokens > 9
            const shouldShowUpBuy = isAuthenticated && !hasEnoughTokens

            const showPrevious = !isPm && !isProfile
            const showAutoplay = !isPm && isLounge && !!roomID
            const showNext = !isPm && !isProfile
            const showVideoToggle =
                (isPm || isProfile) &&
                isBroadcasting &&
                !fullWindow &&
                !!onVideoToggle
            const showTipMenu = fullWindow && tipmenus > 0
            const showWhiteboard = !isLounge && hasWhiteboard
            const showOpenRoom = isPm || isProfile || isLounge
            const showOpenPM = !isLounge && !isPm
            const showOpenProfile = !fullWindow && (isPm || isRoom)
            const showClose = true
            const showTip = false
            const showEndShow = !isPm ? isUserInNonPublicRoom : isUserInPmShow
            // console.log('[actionsbar]', { isPm, isProfile, isRoom })
            const isUserAllowedToRequest =
                !isAway &&
                !isHiddenShow &&
                !isLounge &&
                !isUserBanned &&
                (!isPm ? !isUserInNonPublicRoom : !isUserInPmShow) &&
                isBroadcasting

            const showGroup =
                !isPm &&
                !isGroupShowDisabled &&
                !isPrivateShow &&
                isUserAllowedToRequest
            const showVoyeur = isPrivateShow && isUserAllowedToRequest
            const showPrivate =
                !isPrivateShow &&
                !isPrivateShowDisabled &&
                !isGroupShow &&
                isUserAllowedToRequest

            const enableNavButtons = isLounge
                ? showAutoplay && modelsLoaded
                : modelsLoaded && !!roomID

            const onCloseCallback = useCallback(() => {
                // route back to the path that's underneath
                closeRoomAndNavBack()
                if (!isPm && !isProfile) {
                    updateRoomState(id, {
                        viewing: false,
                    })
                    return
                } else {
                    setPMChatIntentionallyClosedState(id, true)
                }
            }, [isPm, isProfile, id, slug])

            const onClickShareTipMenu = useCallback(() => {
                if (!isAuthenticated) {
                    openAuthenticationDrawer()
                    return
                }
                openShareTipsDrawer({ name })
            }, [name, isAuthenticated])

            const onOpenTip = useCallback(() => {
                openTipsDrawer({
                    slug,
                    hiddenShows: true,
                    chatError: true,
                })
            }, [slug])

            const onPrevious = useCallback(async () => {
                if (typeof onPreviousRoom === 'function') {
                    onPreviousRoom()
                }
                if (isLounge) {
                    setPreviousSurrogate(id)
                } else {
                    const prevID = getNextOrPreviousModel(id, {
                        previous: true,
                    })
                    await loadUserIfNeeded(prevID)
                    openRoom(prevID)
                }
            }, [id, isLounge])

            const onNext = useCallback(async () => {
                if (typeof onNextRoom === 'function') {
                    onNextRoom()
                }
                if (isLounge) {
                    setNextSurrogate(id)
                } else {
                    const nextID = getNextOrPreviousModel(id)
                    await loadUserIfNeeded(nextID)
                    openRoom(nextID)
                }
            }, [id, isLounge])

            const onClickOpenRoom = useCallback(() => {
                openRoom(slug)
                onOpenRoom?.()
            }, [slug, onOpenRoom])

            const onClickSendPM = useCallback(() => {
                if (!isAuthenticated) {
                    openAuthenticationDrawer()
                    return
                }
                // openPm(slug)
                return openMessage(slug)
            }, [slug, isAuthenticated])

            const onClickOpenProfile = useCallback(() => {
                if (isLounge) {
                    openProfileDrawer(slug)
                } else {
                    return openProfile(slug)
                }
            }, [slug])

            const showUpBuyMessage = useCallback((showType: StreamType) => {
                openConfirmation({
                    title: i18n.t('chat.addTokens'),
                    message: <UpBuyMessage showType={showType} />,
                    onOk: () => openTokensDrawer(),
                    cancel: true,
                })
            }, [])

            const confirmationMessage = useCallback(
                (message: ReactNode): ReactNode =>
                    isAuthenticated ? message : <NotAuthenticatedMessage />,
                [isAuthenticated],
            )

            const onConfirmationOk = useCallback(
                (showType: StreamType) => () => {
                    if (!isAuthenticated) {
                        openAuthenticationDrawer()
                        return
                    }
                    requestTokenShow(showType)(slug)
                },
                [isAuthenticated, slug],
            )

            const onClickEndShow = useCallback(() => {
                streamPublicShow(foundModelID)
            }, [foundModelID])

            const onClickPrivate = useCallback(() => {
                if (shouldShowUpBuy) {
                    showUpBuyMessage('private')
                } else {
                    if (isPrivateShowDisabled) return
                    openConfirmation({
                        message: confirmationMessage(
                            <TruePrivateSwitch
                                isTruePrivateShowEnabled={
                                    isTruePrivateShowEnabled
                                }
                            />,
                        ),
                        onOk: onConfirmationOk('private'),
                        cancel: true,
                    })
                }
            }, [
                confirmationMessage,
                onConfirmationOk,
                shouldShowUpBuy,
                isPrivateShowDisabled,
                isTruePrivateShowEnabled,
                slug,
            ])

            const onClickGroup = useCallback(() => {
                if (shouldShowUpBuy) {
                    showUpBuyMessage('group')
                } else {
                    if (isGroupShowDisabled) return
                    openConfirmation({
                        message: confirmationMessage(
                            isGroupShowPending || !isGroupShow
                                ? i18n.t('chat.confirmRequestGroupShow')
                                : i18n.t('chat.confirmJoinGroupShow'),
                        ),
                        onOk: onConfirmationOk('group'),
                        cancel: !isUserWaiting,
                    })
                }
            }, [
                confirmationMessage,
                onConfirmationOk,
                shouldShowUpBuy,
                isGroupShowDisabled,
                isGroupShowPending,
                isGroupShow,
                isUserWaiting,
                slug,
            ])

            const onClickVoyeur = useCallback(() => {
                if (shouldShowUpBuy) {
                    showUpBuyMessage('voyeur')
                } else {
                    openConfirmation({
                        message: confirmationMessage(
                            i18n.t('chat.confirmRequestVoyeurShow'),
                        ),
                        onOk: onConfirmationOk('voyeur'),
                        cancel: true,
                    })
                }
            }, [confirmationMessage, onConfirmationOk, shouldShowUpBuy, slug])

            const disablePrivateButton = isUserInPrivateRoom || requesting

            useEffect(() => {
                return registerKeyHandler(
                    KeyEvent.Keydown,
                    () => {
                        if (showPrevious) onPrevious()
                    },
                    ['ArrowLeft'],
                )
            }, [onPrevious, showPrevious])

            useEffect(() => {
                return registerKeyHandler(
                    KeyEvent.Keydown,
                    () => {
                        if (showNext) onNext()
                    },
                    ['ArrowRight'],
                )
            }, [onNext, showNext])

            return (
                <div
                    ref={ref}
                    css={
                        verticalLayout && fullWindow
                            ? fullWindowVerticalStyles
                            : fullWindow
                              ? fullWindowHorizontalStyles
                              : chatActionBarStyles
                    }
                    data-testid="Root__ChatActionsBar"
                >
                    {showPrevious && (
                        <ChatActionButton
                            testId="IconButton__PreviousModel"
                            label={i18n.t('chat.previous')}
                            disabled={!enableNavButtons}
                            onClick={onPrevious}
                        >
                            <ChevronLeft />
                        </ChatActionButton>
                    )}
                    {showAutoplay && (
                        <>
                            <AutoPlayActionButton
                                id={id}
                                fullWindow={fullWindow}
                            />
                            <ChatActionButton
                                testId="IconButton__AutoPlayOptions"
                                label={i18n.t('chat.autoPlayOptions')}
                                onClick={openAutoplayOptionsDrawer}
                            >
                                <AutoPlayOptions />
                            </ChatActionButton>
                        </>
                    )}
                    {showNext && (
                        <ChatActionButton
                            testId="IconButton__NextModel"
                            label={i18n.t('chat.next')}
                            disabled={!enableNavButtons}
                            onClick={onNext}
                        >
                            <ChevronRight />
                        </ChatActionButton>
                    )}
                    {showVideoToggle && (
                        <ChatActionButton
                            testId="IconButton__ToggleStream"
                            label={
                                video
                                    ? i18n.t('chat.hideVideo')
                                    : i18n.t('chat.showVideo')
                            }
                            onClick={onVideoToggle}
                        >
                            <Videocam active={video} />
                        </ChatActionButton>
                    )}
                    {showTipMenu && (
                        <ChatActionButton
                            testId="ChatActionsBar__TipMenu"
                            label={i18n.t('chat.tipMenu')}
                            onClick={onClickShareTipMenu}
                        >
                            <TipMenu />
                        </ChatActionButton>
                    )}
                    {showWhiteboard && (
                        <ChatActionButton
                            testId="ChatActionsBar__Whiteboard"
                            label={i18n.t('chat.whiteboard')}
                            disabled={!hasWhiteboard}
                            onClick={onWhiteboard}
                        >
                            <Whiteboard filled={isWhiteboardOpen} />
                        </ChatActionButton>
                    )}
                    {showOpenRoom && (
                        <ChatActionButton
                            testId="ChatActionsBar__OpenRoom"
                            label={i18n.t('chat.goToRoom')}
                            onClick={onClickOpenRoom}
                        >
                            <Room />
                        </ChatActionButton>
                    )}
                    {showOpenPM && (
                        <ChatActionButton
                            testId="ChatActionsBar__OpenPM"
                            label={i18n.t('chat.sendPM')}
                            onClick={onClickSendPM}
                        >
                            <MessageIcon unreadPM={unreadPM} />
                        </ChatActionButton>
                    )}
                    {showEndShow && (
                        <ChatActionButton
                            testId="IconButton__EndShow"
                            label={i18n.t('chat.endShow')}
                            onClick={onClickEndShow}
                        >
                            <Exit title={i18n.t('chat.endShow')} />
                        </ChatActionButton>
                    )}
                    {showPrivate && (
                        <ChatActionButton
                            testId="IconButton__Private"
                            label={i18n.t('chat.private')}
                            disabled={disablePrivateButton}
                            onClick={onClickPrivate}
                        >
                            <Private />
                        </ChatActionButton>
                    )}
                    {showVoyeur && (
                        <ChatActionButton
                            testId="IconButton__Voyeur"
                            label={i18n.t('chat.spy')}
                            disabled={isUserInVoyeurRoom}
                            onClick={onClickVoyeur}
                        >
                            <Spy />
                        </ChatActionButton>
                    )}
                    {showGroup && (
                        <ChatActionButton
                            testId="IconButton__Group"
                            label={i18n.t('chat.group')}
                            disabled={isUserWaiting || isUserInGroupRoom}
                            onClick={onClickGroup}
                        >
                            <GroupShowIcon
                                countUsersRequesting={
                                    countUsersRequestingGroupShow
                                }
                            />
                        </ChatActionButton>
                    )}
                    {showTip && (
                        <ChatActionButton
                            testId="IconButton__Tip"
                            label={i18n.t('chat.tip')}
                            onClick={onOpenTip}
                        >
                            <Tip />
                        </ChatActionButton>
                    )}
                    {showOpenProfile && (
                        <ChatActionButton
                            testId="IconButton__OpenProfile"
                            label={i18n.t('chat.profile')}
                            onClick={onClickOpenProfile}
                        >
                            <AccountDetails />
                        </ChatActionButton>
                    )}
                    {/* {showOpenMenu && (
                        <ChatActionButton
                            testId="IconButton__OpenMenu"
                            label={i18n.t('chat.menu')}
                            onClick={onMenu}
                        >
                            <DotMenu />
                        </ChatActionButton>
                    )} */}
                    {showClose && (
                        <ChatActionButton
                            testId="IconButton__Close"
                            onClick={onCloseCallback}
                            styleOverrides={closeButtonStyles}
                        >
                            <Close />
                        </ChatActionButton>
                    )}
                </div>
            )
        },
    ),
)

export default ChatActionsBar
